import { AppId } from '@inno-tools/container-bridge'
import { AvailableApps } from 'modules/app-registry/hooks/useAvailableApps'
import { appConfigs } from 'shared/configs/appConfigs'
import { UserRole } from 'shared/types/user'

const adminRole = process.env.REACT_APP_ADMIN_ROLE_NAME!
const employeeRole = process.env.REACT_APP_EMPLOYEE_ROLE_NAME!
const finanzenRole = process.env.REACT_APP_FINANZEN_ROLE_NAME!
const pmRole = process.env.REACT_APP_PM_ROLE_NAME!

/** How a shortcut is declared in config: which app and who gets it. */
type DefaultShortcutDefinition = {
  appId: AppId
  /**
   * When set, the shortcut is only provisioned for users holding one of these
   * roles. Leave empty for core shortcuts that every user gets.
   */
  requiredRoles: string[]
  /**
   * Ordered alternative `AppId`s to try when the user/org has no access to
   * `appId`. Used for apps shipped in multiple variants where different
   * orgs may be granted access to different ones (e.g. PROJECT_MANAGEMENT_V2
   * vs PROJECT_MANAGEMENT). The first candidate the org has access to wins.
   */
  fallbackAppIds?: AppId[]
}

/** A resolved shortcut with its route and default app metadata applied. */
export type DefaultShortcutConfig = DefaultShortcutDefinition & {
  /** Display name sent to the backend as the shortcut/item name. */
  name: string
  /** App route used to build the internal shortcut URL (e.g. '/cluster-files'). */
  path: string
}

/**
 * Default shortcuts provisioned once per user on their first session, similar to
 * how a fresh OS install seeds default items.
 *
 * App names and paths come from `appConfigs`, resolved lazily (see
 * `getShortcutsForUser`) so they do not drift. To add a shortcut, append an
 * entry. Role-gated shortcuts are only created for users holding one of
 * `requiredRoles`.
 */
const DEFAULT_SHORTCUT_DEFINITIONS: DefaultShortcutDefinition[] = [
  { appId: AppId.PROFILE, requiredRoles: [] },
  { appId: AppId.CLUSTER_FILES, requiredRoles: [] },
  { appId: AppId.CLUSTER_DOCS, requiredRoles: [] },
  { appId: AppId.CLUSTER_CELLS, requiredRoles: [] },
  { appId: AppId.CLUSTER_SLIDES, requiredRoles: [] },
  { appId: AppId.CLUSTER_PDF, requiredRoles: [] },
  { appId: AppId.SUBSIDIES, requiredRoles: [adminRole] },
  {
    appId: AppId.PROJECT_MANAGEMENT_V2,
    fallbackAppIds: [AppId.PROJECT_MANAGEMENT],
    requiredRoles: [pmRole, employeeRole]
  },
  { appId: AppId.MY_ORGANIZATION, requiredRoles: [finanzenRole] }
]

/**
 * Resolves each definition's `name`/`path` from `appConfigs`.
 *
 * Deliberately NOT computed at module load: `appConfigs` pulls in the
 * app-registry barrel, and reading it during this module's initialization can
 * hit a temporal-dead-zone error under circular imports. Calling it lazily (from
 * `getShortcutsForUser`, invoked at runtime) guarantees `appConfigs` is fully
 * initialized.
 *
 * Apps filtered out of `appConfigs` (because their `REACT_APP_*_DEV_PATH`
 * env var or their `runtimeEnvKey` entry in `__MFE_ENV_SCRIPTS__` is
 * missing/empty) have no shortcut to seed — skip them rather than dereferencing
 * `undefined`.
 *
 * When `availableApps` is provided, definitions that declare
 * `fallbackAppIds` (multi-variant apps) are resolved to whichever variant the
 * org/user has access to: the first candidate (primary, then fallbacks in
 * declared order) present in `availableApps` and not `isDisabled` (i.e. not
 * locked) wins. Locked and missing variants are skipped. This is how PM V2 is
 * swapped for PM V1 for orgs without a V2 grant, instead of always seeding V2.
 *
 * Without `availableApps` (dev/test fallback), variant resolution falls back
 * to whichever candidate is configured locally — preserving the prior
 * "whichever env has it" behavior for non-prod callers.
 */
const resolveDefaultShortcuts = (
  availableApps?: AvailableApps
): DefaultShortcutConfig[] =>
  DEFAULT_SHORTCUT_DEFINITIONS.flatMap(
    ({ appId, requiredRoles, fallbackAppIds }) => {
      // Single-variant entries use the env-driven appConfigs lookup.
      if (!fallbackAppIds?.length) {
        const config = appConfigs[appId]
        if (!config) return []
        return [
          {
            appId,
            requiredRoles,
            name: config.name,
            path: config.path
          }
        ]
      }

      // Multi-variant entries: pick the first variant the user/org has
      // access to (per `availableApps`), or fall back to the first variant
      // configured locally when org access info is not yet available.
      const candidates: AppId[] = [appId, ...fallbackAppIds]
      const resolvedId = availableApps
        ? candidates.find(id => {
            const access = availableApps[id]
            return access !== undefined && !access.isDisabled
          })
        : candidates.find(id => appConfigs[id] !== undefined)

      if (!resolvedId) return []
      const config = appConfigs[resolvedId]
      if (!config) return []
      return [
        {
          appId: resolvedId,
          requiredRoles,
          name: config.name,
          path: config.path
        }
      ]
    }
  )

/**
 * Returns the default shortcuts applicable to a user: all core shortcuts plus any
 * role-gated shortcuts whose `requiredRoles` the user holds.
 */
export const getShortcutsForUser = (
  roles: UserRole[] = [],
  availableApps?: AvailableApps
): DefaultShortcutConfig[] =>
  resolveDefaultShortcuts(availableApps).filter(
    shortcut =>
      !shortcut.requiredRoles.length ||
      roles.some(role => shortcut.requiredRoles.includes(role))
  )
